"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CartService = void 0;
const axios_1 = __importDefault(require("axios"));
const environment_1 = require("../../constants/environment");
const environment_enum_1 = require("../enum/environment.enum");
const order_enum_1 = require("../enum/order.enum");
class CartService {
    constructor(ENV = environment_enum_1.ENVIRONMENT.DEV, key) {
        this.RESOURCE = 'carts';
        /**
         * Crear un carrito para una persona. Si existe un carrito activo, se devolverá el carrito existente.
         * @param personId. (Number) 'id de la persona'.
         * @param paymentToken (opcional). (String) 'token de pago'.
         * @param admissionId (opcional). (String) 'admision id de crm'.
         */
        this.createCart = async (personId, paymentToken, admissionId) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}`;
            // Crear el payload para la solicitud
            const payload = { person_id: personId };
            // Añadir payment_token si se proporciona
            if (paymentToken) {
                payload.payment_token = paymentToken;
            }
            // Añadir admission_id si se proporciona
            if (admissionId) {
                payload.admission_id = admissionId;
            }
            const response = await axios_1.default.post(URL, JSON.stringify(payload), { headers: { 'x-api-key': this.KEY } });
            return (response.status === 200) ? Promise.resolve(response.data) : Promise.reject(response.statusText);
        };
        /**
         * Crear un carrito en base a una sesión anónima. Si ya existe un carrito asociado al identificador de sesión, se devolverá el carrito existente.
         * @param sessionId. (Number) 'id de la sesión'.
         */
        this.createCartFromSession = async (sessionId) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}`;
            const response = await axios_1.default.post(URL, JSON.stringify({ session_id: sessionId }), { headers: { 'x-api-key': this.KEY } });
            return (response.status === 200) ? Promise.resolve(response.data) : Promise.reject(response.statusText);
        };
        /**
         * Obtiene un carrito y la lista de productos en base al id especificado.
         * @param id. (Number) 'id del carrito'.
         */
        this.getCartById = async (id) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/${id}`;
            const response = await axios_1.default.get(URL, { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(this.calculateCartTotals(response.data));
            }
            return Promise.reject(response.statusText);
        };
        /**
         * Obtiene el carrito activo de una persona.
         * @param personId. (Number) 'id de la persona'.
         */
        this.getCartByPersonId = async (personId) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/person/${personId}`;
            const response = await axios_1.default.get(URL, { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(this.calculateCartTotals(response.data));
            }
            return Promise.reject(response.statusText);
        };
        /**
         * Obtiene el carrito según un id de sesión.
         * @param sessionId. (Number) 'id de la sesión'.
         */
        this.getCartBySessionId = async (sessionId) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/session/${sessionId}`;
            const response = await axios_1.default.get(URL, { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(this.calculateCartTotals(response.data));
            }
            return Promise.reject(response.statusText);
        };
        /**
         * Obtiene los carritos activos de una persona.
         * @param personId. (Number) 'id de la persona'.
         */
        this.getAllCartsByPersonId = async (personId) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/person/${personId}?all=true`;
            const response = await axios_1.default.get(URL, { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                const carts = [];
                for (const data of response.data) {
                    const cart = this.calculateCartTotals(data);
                    carts.push(cart);
                }
                return Promise.resolve(carts);
            }
            return Promise.reject(response.statusText);
        };
        /**
         * Agrega un producto al carrito.
         * @param cartId. (Number) 'id del carrito'.
         * @param product. (Object) 'detalle del producto'.
         */
        this.addProduct = async (cartId, product) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/${cartId}/products`;
            const response = await axios_1.default.post(URL, JSON.stringify([product]), { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(this.calculateCartTotals(response.data));
            }
            return Promise.reject(response.statusText);
        };
        /**
         * Agrega productos al carrito.
         * @param cartId. (Number) 'id del carrito'.
         * @param products. (Array) 'lista con los detalles de los productos'.
         */
        this.addProducts = async (cartId, products) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/${cartId}/products`;
            const response = await axios_1.default.post(URL, JSON.stringify(products), { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(this.calculateCartTotals(response.data));
            }
            return Promise.reject(response.statusText);
        };
        /**
         * Actualiza la información de un producto en el carrito.
         * @param cartId. (Number) 'id del carrito'.
         * @param productId. (Number) 'id del producto'.
         * @param detail. (Object) 'detalle del producto'.
         */
        this.updateProduct = async (cartId, productId, detail) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/${cartId}/products/${productId}`;
            const response = await axios_1.default.put(URL, JSON.stringify(detail), { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(this.calculateCartTotals(response.data));
            }
            return Promise.reject(response.statusText);
        };
        /**
         * Actualiza la persona en un carrito especifico.
         * @param cartId. (Number) 'id del carrito'.
         * @param personId. (Number) 'id de la persona'.
         */
        this.updatePersonInCart = async (cartId, personId) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/${cartId}/person`;
            const response = await axios_1.default.put(URL, JSON.stringify({ person_id: personId }), { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(this.calculateCartTotals(response.data));
            }
            return Promise.reject(response.statusText);
        };
        /**
         * Elimina un producto del carrito.
         * @param cartId. (Number) 'id del carrito'.
         * @param productId. (Number) 'id del producto'.
         */
        this.deleteProduct = async (cartId, productId) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/${cartId}/products/${productId}`;
            const response = await axios_1.default.delete(URL, { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(this.calculateCartTotals(response.data));
            }
            return Promise.reject(response.statusText);
        };
        /**
         * Calcula los totales de un objeto Cart.
         * @param cart. (Object) 'objeto cart'.
         */
        this.calculateCartTotals = (cart) => {
            let total_discounts = cart.cart_details.reduce((acc, detail) => acc + detail.discount, 0);
            // Calcular el subtotal sumando el precio unitario de cada producto multiplicado por la cantidad
            let subtotal = cart.cart_details.reduce((acc, detail) => acc + detail.unit_price * detail.quantity, 0);
            // Calcular el total aplicando los descuentos si existen
            let total = subtotal - total_discounts;
            // Asegurarse de que el total no sea negativo
            total = Math.max(0, total);
            // Actualizar el objeto de carrito con los nuevos valores calculados
            return Object.assign(Object.assign({}, cart), { subtotal, total, total_discounts });
        };
        /**
         * Procesar el carrito a checkout
         * @param cartId (number)
         * @returns (Object) ResShoppingCartProcess
         */
        this.shoppingCartProcess = async (cartId, personId, person_info) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/${cartId}/process`;
            const status_id = order_enum_1.ORDER_STATUS.CHECKOUT;
            const order_type_id = order_enum_1.ORDER_TYPES.SALE;
            const response = await axios_1.default.post(URL, JSON.stringify({ personId, status_id, order_type_id, person_info }), { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
            return Promise.reject(response.statusText);
        };
        /**
         * Método para procesar pago
         *
         * @param cartId {number} id de la orden generada
         * @param payment_gateway_method_id {number} identidicador de la relación de la pasarela de pago con la forma de pago
         * @param payment_tokens  {Array}(opcional) solo aplica para pagos con tarjetas  object {token:string, tenant:string}
         * @param monthly_payment_months {number} (opcional) aplica para pagos de meses sin intereses.
         * @param payment_gateway_env {number} (opcional) ambiente explicito para la pasarela de pagos.
         * @returns {object} ResShoppingCartPay
         */
        this.shoppingCartPay = async (cartId, payment_gateway_method_id, payment_tokens, monthly_payment_months, payment_gateway_env) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/${cartId}/pay`;
            const response = await axios_1.default.post(URL, JSON.stringify({ payment_gateway_method_id, payment_tokens, monthly_payment_months, payment_gateway_env }), { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
            return Promise.reject(response.statusText);
        };
        /**
        * Método para Confirmar el pago por 3DS
        *
        * @param cartId {number} id de la orden generada
        * @param orderStatusId {number} identidicador del nuevo estatus
        * @returns {object} ResShoppingCartPayConfirm
        */
        this.shoppingCartPayConfirm = async (cartId, orderStatusId, metadata = {}) => {
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/${cartId}/pay/confirm`;
            const response = await axios_1.default.post(URL, JSON.stringify({ orderStatusId, metadata }), { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
            return Promise.reject(response.statusText);
        };
        /**
         * Apply coupon in the checkout
         * @param cartId {number} id order
         * @param coupon {string} code coupon
         * @returns {object}
         */
        this.couponCheckerCheckout = async (cartId, coupon) => {
            var _a;
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/${cartId}/coupons`;
            const response = await axios_1.default.post(URL, JSON.stringify({ coupon, action: 'checker' }), { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
            else {
                const data = (_a = response === null || response === void 0 ? void 0 : response.response) === null || _a === void 0 ? void 0 : _a.data;
                const error = data || response.statusText;
                return Promise.reject(error);
            }
            ;
        };
        /**
         * Remove coupon from order at checkout
         * @param cartId {number} id order
         * @param couponId {number} id coupon
         * @returns {object}
         */
        this.removeCouponCheckout = async (cartId, couponId) => {
            var _a;
            const URL = `${environment_1.API_URL_BASE}/${this.ENV}/${this.RESOURCE}/${cartId}/coupons`;
            const response = await axios_1.default.put(URL, JSON.stringify({ action: 'remove', couponId }), { headers: { 'x-api-key': this.KEY } });
            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
            else {
                const data = (_a = response === null || response === void 0 ? void 0 : response.response) === null || _a === void 0 ? void 0 : _a.data;
                const error = data || response.statusText;
                return Promise.reject(error);
            }
            ;
        };
        this.ENV = ENV;
        this.KEY = key;
    }
}
exports.CartService = CartService;
